import { FormEvent, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { httpsCallable } from "firebase/functions";
import Alert from "react-bootstrap/esm/Alert";
import Button from "react-bootstrap/esm/Button";
import Card from "react-bootstrap/esm/Card";
import Container from "react-bootstrap/esm/Container";
import Form from "react-bootstrap/esm/Form";
import Spinner from "react-bootstrap/esm/Spinner";
import { useTranslation } from "react-i18next";
import { functions } from "../services/firebase";
import I_IMC_Props from "../infos/I_IMC_Props";
import {
    RECORDING_CATEGORY,
    buildRecordingFolderName,
    buildRecordingTrackNames,
    RecordingServiceData,
    RecordingTrack,
} from "../utils/recordingNames";

export interface RecordingServiceLoaderEntry {
    serviceType?: string;
    serviceLabel?: string;
    serviceDate: string;
    sermon1: RecordingServiceData["sermon1"];
    sermon2: RecordingServiceData["sermon2"];
    choirSongs: RecordingServiceData["choirSongs"];
    categoryCode?: string;
    categoryLabel?: string;
    locationCode?: string;
    locationLabel?: string;
}

export type RecordingServiceLoaderResult = RecordingServiceLoaderEntry[];

type RecordingNamesPageProps = {
    serviceLoader?: (gemeindeSlug: string, password: string) => Promise<RecordingServiceLoaderResult | null>;
    title?: string;
} & Partial<I_IMC_Props>;

type LoadState = "idle" | "loading" | "ready" | "error";

type RecordingServiceViewModel = RecordingServiceData & {
    serviceType?: string;
    serviceLabel?: string;
};

export async function loadRecordingServiceData(
    gemeindeSlug: string,
    password: string,
): Promise<RecordingServiceLoaderResult | null> {
    const loadService = httpsCallable<{ gemeindeSlug: string; password: string }, RecordingServiceLoaderResult | null>(
        functions,
        "getRecordingServiceData",
    );

    const result = await loadService({ gemeindeSlug, password });
    return result.data;
}

function formatServiceDate(serviceDate: string, locale: string): string {
    const date = new Date(`${serviceDate}T00:00:00`);
    return date.toLocaleDateString(locale, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

function mapToViewModel(data: RecordingServiceLoaderEntry): RecordingServiceViewModel {
    return {
        serviceType: data.serviceType,
        serviceLabel: data.serviceLabel,
        serviceDate: data.serviceDate,
        categoryCode: data.categoryCode ?? RECORDING_CATEGORY.code,
        categoryLabel: data.categoryLabel ?? RECORDING_CATEGORY.label,
        locationCode: data.locationCode ?? "",
        locationLabel: data.locationLabel ?? "",
        sermon1: data.sermon1,
        sermon2: data.sermon2,
        choirSongs: data.choirSongs,
    };
}

async function copyText(text: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
}

function localizeTrackLabels(t: (key: string, options?: any) => string, tracks: RecordingTrack[]) {
    return tracks.map((track) => {
        const choirSongMatch = track.sourceLabel.match(/^(?:Chorlied|Choir song)\s+(\d+)$/i);

        if (choirSongMatch) {
            return {
                ...track,
                localizedSourceLabel: t("recordingNames.choirSongWithSlot", {
                    slot: choirSongMatch[1],
                }),
            };
        }

        if (/^(?:Predigt|Sermon)\s+1$/i.test(track.sourceLabel)) {
            return {
                ...track,
                localizedSourceLabel: t("recordingNames.sermon1"),
            };
        }

        if (/^(?:Predigt|Sermon)\s+2$/i.test(track.sourceLabel)) {
            return {
                ...track,
                localizedSourceLabel: t("recordingNames.sermon2"),
            };
        }

        return {
            ...track,
            localizedSourceLabel: track.sourceLabel,
        };
    });
}

export default function RecordingNamesPage({
    serviceLoader = loadRecordingServiceData,
    title,
}: RecordingNamesPageProps) {
    const { gemeindeSlug } = useParams();
    const { t, i18n } = useTranslation();
    const [passwordInput, setPasswordInput] = useState("");
    const [state, setState] = useState<LoadState>("idle");
    const [errorMessage, setErrorMessage] = useState("");
    const [services, setServices] = useState<RecordingServiceViewModel[]>([]);
    const [copyMessage, setCopyMessage] = useState("");
    const [copyError, setCopyError] = useState("");

    const localizedTitle = useMemo(() => title?.trim() || t("recordingNames.title"), [title, t]);

    const dateLocale = useMemo(() => {
        if (i18n.resolvedLanguage?.toLowerCase().startsWith("en")) {
            return "en-US";
        }

        return "de-DE";
    }, [i18n.resolvedLanguage]);

    useEffect(() => {
        document.title = localizedTitle;
    }, [localizedTitle]);

    async function handleUnlock(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setState("loading");
        setErrorMessage("");
        setCopyMessage("");
        setCopyError("");

        const enteredPassword = passwordInput.trim();

        if (!enteredPassword) {
            setState("error");
            setErrorMessage(String(t("recordingNames.passwordRequired")));
            return;
        }

        try {
            const serviceData = await serviceLoader(gemeindeSlug ?? "", enteredPassword);
            if (!serviceData || serviceData.length === 0) {
                setState("error");
                setErrorMessage(String(t("recordingNames.noService")));
                return;
            }

            setServices(serviceData.map(mapToViewModel));
            setState("ready");
        } catch (err) {
            setState("error");
            if ((err as { code?: string })?.code === "functions/resource-exhausted") {
                setErrorMessage(String(t("errors.tooManyRequests")));
            } else {
                setErrorMessage(err instanceof Error ? err.message : String(t("errors.anErrorOccurred")));
            }
        }
    }

    async function handleCopy(value: string, label: string) {
        try {
            await copyText(value);
            setCopyError("");
            setCopyMessage(String(t("recordingNames.copiedWithLabel", { label })));
        } catch {
            setCopyMessage("");
            setCopyError(String(t("recordingNames.copyFailedWithLabel", { label })));
        }
    }

    return (
        <Container className="py-4" style={{ maxWidth: "900px" }}>
            <div className="mb-4">
                <div className="gl-meta mb-2">{t("recordingNames.recordingSession")} · passwortgeschützt</div>
                <h1 className="mb-1">{localizedTitle}</h1>
                <p className="text-body-secondary mb-0">
                    {t("recordingNames.copyInstructions", "Tippe auf Kopieren, um den Namen in die Zwischenablage zu übernehmen.")}
                </p>
            </div>

            {state !== "ready" ? (
                <Card>
                    <Card.Body>
                        <Form onSubmit={handleUnlock}>
                            <Form.Group className="mb-3" controlId="recording-password">
                                <Form.Label>{t("recordingNames.password")}</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={passwordInput}
                                    onChange={(e) => setPasswordInput(e.target.value)}
                                    placeholder={String(t("recordingNames.passwordPlaceholder"))}
                                    data-testid="recording-password-input"
                                    autoComplete="current-password"
                                />
                            </Form.Group>
                            <Button type="submit" variant="primary" disabled={state === "loading"} data-testid="recording-unlock-button">
                                {state === "loading" ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                                        {t("recordingNames.unlocking")}
                                    </>
                                ) : (
                                    t("recordingNames.unlock")
                                )}
                            </Button>
                        </Form>

                        {state === "error" && (
                            <Alert className="mt-3 mb-0" variant="danger" data-testid="recording-error">
                                {errorMessage}
                            </Alert>
                        )}
                    </Card.Body>
                </Card>
            ) : (
                <div className="d-grid gap-4">
                    {services.map((service, index) => {
                        const trackNames = buildRecordingTrackNames(service);
                        const localizedTrackNames = localizeTrackLabels(
                            (key, options) => String(t(key, options as any)),
                            trackNames
                        );
                        const folderName = buildRecordingFolderName(service, trackNames.length);
                        const serviceTitle =
                            service.serviceLabel ||
                            `${t("recordingNames.serviceBlock")} ${index + 1}`;

                        return (
                            <Card key={`${service.serviceDate}_${serviceTitle}_${index}`}>
                                <Card.Body>
                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <strong>{serviceTitle}</strong>
                                            <span className="gl-badge gl-badge--accent">{t("recordingNames.live")}</span>
                                        </div>
                                        <span className="gl-meta gl-mono">
                                            {formatServiceDate(service.serviceDate, dateLocale)}
                                        </span>
                                    </div>

                                    <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 align-items-start mb-3">
                                        <div className="flex-grow-1">
                                            <div className="gl-meta mb-1">{t("recordingNames.folderName")}</div>
                                            <div
                                                className="gl-mono"
                                                style={{ fontSize: "1rem", color: "var(--gl-ink)", wordBreak: "break-all" }}
                                                data-testid={`recording-folder-name-${index}`}
                                            >
                                                {folderName}
                                            </div>
                                        </div>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleCopy(folderName, `${t("recordingNames.folderName")} (${serviceTitle})`)}
                                            data-testid={`recording-copy-folder-${index}`}
                                        >
                                            {t("recordingNames.copyFolder")}
                                        </Button>
                                    </div>

                                    <div className="rn-meta gl-meta small mb-3">
                                        <div><strong>{t("recordingNames.category")}:</strong> {service.categoryLabel ?? RECORDING_CATEGORY.label} ({service.categoryCode ?? RECORDING_CATEGORY.code})</div>
                                        <div><strong>{t("recordingNames.location")}:</strong> {service.locationLabel || "—"}{service.locationCode ? ` (${service.locationCode})` : ""}</div>
                                        <div>
                                            <strong>{t("recordingNames.sermon1")}:</strong>{" "}
                                            {service.sermon1.topic || service.sermon1.biblePassage || "-"}
                                            {service.sermon1.preacher && <> · <span className="gl-mono">{service.sermon1.biblePassage}</span> · {service.sermon1.preacher.name} {service.sermon1.preacher.surname}</>}
                                        </div>
                                        {service.sermon2 && (
                                            <div>
                                                <strong>{t("recordingNames.sermon2")}:</strong>{" "}
                                                {service.sermon2.topic || service.sermon2.biblePassage || "-"}
                                                {service.sermon2.preacher && <> · <span className="gl-mono">{service.sermon2.biblePassage}</span> · {service.sermon2.preacher.name} {service.sermon2.preacher.surname}</>}
                                            </div>
                                        )}
                                    </div>

                                    <div className="rn-tracks">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <strong>{t("recordingNames.trackNames")}</strong>
                                            <span className="gl-meta">{t("recordingNames.entriesCount", { count: localizedTrackNames.length })}</span>
                                        </div>
                                        {localizedTrackNames.length === 0 ? (
                                            <p className="text-muted small mb-0">{t("recordingNames.noTrackData")}</p>
                                        ) : (
                                            <ul className="list-unstyled m-0">
                                                {localizedTrackNames.map((track) => (
                                                    <li key={`${index}-${track.slot}`} className="rn-row">
                                                        <div className="flex-grow-1">
                                                            <div className="gl-meta small mb-1">{track.localizedSourceLabel}</div>
                                                            <div className="rn-name" data-testid={`recording-track-${index}-${track.slot}`}>{track.generatedName}</div>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="btn-ghost"
                                                            onClick={() => handleCopy(track.generatedName, `${track.localizedSourceLabel} (${serviceTitle})`)}
                                                            data-testid={`recording-copy-track-${index}-${track.slot}`}
                                                        >
                                                            {t("recordingNames.copyTrack")}
                                                        </Button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    <div
                                        className={`rn-toast gl-meta${copyMessage ? " is-show" : ""}${copyError ? " is-error" : ""}`}
                                        aria-live="polite"
                                        data-testid={copyMessage ? "recording-copy-success" : copyError ? "recording-copy-error" : undefined}
                                    >
                                        {copyMessage || copyError}
                                    </div>
                                </Card.Body>
                            </Card>
                        );
                    })}
                </div>
            )}
        </Container>
    );
}
